import { ErrorLogComponent } from '@cian/error-log-component';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { NewbuildingPurchaseGiftExperiment } from './NewbuildingPurchaseGiftExperiment';
import { NewbuildingPurchaseGiftModalLoadable } from './NewbuildingPurchaseGiftModalLoadable';
import {
  INewbuildingPurchaseGiftPromoContext,
  NewbuildingPurchaseGiftPromoContext,
  TNewbuildingPurchaseGiftPromoSource,
} from './NewbuildingPurchaseGiftPromoContext';
import {
  trackCopyEmail,
  trackEntryPointClick,
  trackEntryPointShow,
  trackModalClose,
  trackModalConfirm,
  trackModalShow,
  trackStepView,
  trackTermsClick,
} from './tracking';
import {
  NEWBUILDING_PURCHASE_GIFT_EMAIL_KEY,
  NEWBUILDING_PURCHASE_GIFT_ENABLED_KEY,
  NEWBUILDING_PURCHASE_GIFT_REGIONS,
  NEWBUILDING_PURCHASE_GIFT_TERMS_URL_KEY,
} from '../../constants/newbuildingPurchaseGiftPromo';
import { getIsLeadFactory } from '../../selectors/newbuilding/getIsLeadFactory';
import { getRegionId } from '../../selectors/newbuilding/getRegionId';
import { useApplicationContext } from '../../utils/applicationContext';

interface INewbuildingPurchaseGiftPromoConfig {
  email: string;
  termsUrl: string;
}

interface IGateProps extends React.PropsWithChildren {
  config: INewbuildingPurchaseGiftPromoConfig;
}

const PromoModalHost: React.FC<IGateProps> = ({ config, children }) => {
  const { logger } = useApplicationContext();
  const [open, setOpen] = React.useState(false);
  /* Чанк модалки грузится только после первого открытия, дальше модалка остаётся смонтированной */
  const [isModalMounted, setIsModalMounted] = React.useState(false);
  const sourceRef = React.useRef<TNewbuildingPurchaseGiftPromoSource>('sidebar');
  const sidebarShownRef = React.useRef(false);

  const openModal = React.useCallback((source: TNewbuildingPurchaseGiftPromoSource) => {
    trackEntryPointClick(source);
    sourceRef.current = source;
    setIsModalMounted(true);
    setOpen(true);
  }, []);

  /* Сайдбар есть в верхнем и нижнем блоках страницы: показ язычка отправляется один раз */
  const trackSidebarShow = React.useCallback(() => {
    if (sidebarShownRef.current) {
      return;
    }

    sidebarShownRef.current = true;
    trackEntryPointShow('sidebar');
  }, []);

  const contextValue = React.useMemo<INewbuildingPurchaseGiftPromoContext>(
    () => ({ isAvailable: true, openModal, trackSidebarShow }),
    [openModal, trackSidebarShow],
  );

  const handleShow = React.useCallback(() => trackModalShow(sourceRef.current), []);

  return (
    <NewbuildingPurchaseGiftPromoContext.Provider value={contextValue}>
      {children}
      {isModalMounted && (
        <ErrorLogComponent logger={logger}>
          <NewbuildingPurchaseGiftModalLoadable
            open={open}
            onOpenChange={setOpen}
            config={config}
            onShow={handleShow}
            onClose={trackModalClose}
            onConfirmClick={trackModalConfirm}
            onCopyEmailClick={trackCopyEmail}
            onTermsClick={trackTermsClick}
            onStepView={trackStepView}
          />
        </ErrorLogComponent>
      )}
    </NewbuildingPurchaseGiftPromoContext.Provider>
  );
};

const ExperimentGate: React.FC<IGateProps> = ({ config, children }) => {
  const group = NewbuildingPurchaseGiftExperiment.use();

  if (group !== 'B') {
    return children;
  }

  return <PromoModalHost config={config}>{children}</PromoModalHost>;
};

/**
 * Провайдер акции «30 000 ₽ за покупку квартиры» для карточки ЖК:
 * считает доступность точек входа и держит общую модалку для язычка под сайдбаром.
 * На страницах КпН (фабрика лидов) акции нет: продаёт консультант Циана, а не застройщик.
 * Эксперимент читается только когда тогл включён, конфиг заполнен, регион ЖК подходит и это не КпН,
 * чтобы exposure уходил лишь там, где точка входа могла показаться.
 */
export const NewbuildingPurchaseGiftPromoProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { config } = useApplicationContext();
  const regionId = useSelector(getRegionId);
  const isLeadFactory = useSelector(getIsLeadFactory);

  const isEnabled = Boolean(config.get<boolean>(NEWBUILDING_PURCHASE_GIFT_ENABLED_KEY));
  const email = config.get<string>(NEWBUILDING_PURCHASE_GIFT_EMAIL_KEY) || '';
  const termsUrl = config.get<string>(NEWBUILDING_PURCHASE_GIFT_TERMS_URL_KEY) || '';
  const isInRegion = regionId !== null && NEWBUILDING_PURCHASE_GIFT_REGIONS.includes(regionId);
  const promoConfig = React.useMemo<INewbuildingPurchaseGiftPromoConfig>(
    () => ({ email, termsUrl }),
    [email, termsUrl],
  );

  if (!isEnabled || !email || !termsUrl || !isInRegion || isLeadFactory) {
    return children;
  }

  return <ExperimentGate config={promoConfig}>{children}</ExperimentGate>;
};
